import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';

import { AuthProvider } from './context/AuthContext';
import { MaintenanceProvider, useMaintenance } from './context/MaintenanceContext';
import { CartProvider } from './context/CartContext';
import { ProtectedRoute, AdminRoute, PublicOnlyRoute } from './components/auth';
import { Header, Footer } from './components/layout';
import {
  HomePage,
  OngleriePedicurePage,
  BienEtrePage,
  PressOnNailsPage,
  FormationPage,
  GiftCardPage,
  AtelierEvenementielPage,
  MonComptePage,
  OrderDetailPage,
  AdminPage,
  AdminPrestationsPage,
  AdminPressOnNailsPage,
  AdminFeaturedPage,
  AdminSubcategoriesPage,
  AdminGiftCardsPage,
  AdminB2BDocumentsPage,
  AdminFormationsPage,
  AdminReviewsPage,
  AdminOrdersPage,
  AdminOrderDetailPage,
  MentionsLegalesPage,
  PolitiqueConfidentialitePage,
  CGVPage,
  LoginPage,
  RegisterPage,
  PanierPage,
  CheckoutPage,
  CheckoutSuccessPage,
  MaintenancePage
} from './pages';

import './index.css';

const MaintenanceLoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[var(--ivory)] to-[#f5d5d5]">
    <div className="text-center">
      <div className="w-12 h-12 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-sm text-[var(--charcoal)]/60 tracking-[0.25em] uppercase">Chargement...</p>
    </div>
  </div>
);

function MaintenanceGate({ children }) {
  const location = useLocation();
  const { maintenanceEnabled, isLoadingMaintenance } = useMaintenance();

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoginRoute = location.pathname === '/login';
  const isMaintenanceRoute = location.pathname === '/maintenance';
  const isExemptRoute = isAdminRoute || isLoginRoute;

  if (!isExemptRoute && !isMaintenanceRoute && isLoadingMaintenance) {
    return <MaintenanceLoadingScreen />;
  }

  if (maintenanceEnabled && !isExemptRoute && !isMaintenanceRoute) {
    return <Navigate to="/maintenance" replace state={{ from: location }} />;
  }

  if (!maintenanceEnabled && isMaintenanceRoute) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/onglerie-pedicure" element={<OngleriePedicurePage />} />
        <Route path="/bien-etre" element={<BienEtrePage />} />
        <Route path="/press-on-nails" element={<PressOnNailsPage />} />
        <Route path="/carte-cadeau" element={<GiftCardPage />} />
        <Route path="/atelier-evenementiel" element={<AtelierEvenementielPage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
        <Route path="/cgv" element={<CGVPage />} />
        <Route path="/panier" element={<PanierPage />} />
        <Route path="/formation" element={<FormationPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        
        {/* Auth Routes (public only when not logged in) */}
        <Route path="/login" element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        } />
        <Route path="/register" element={
          <PublicOnlyRoute>
            <RegisterPage />
          </PublicOnlyRoute>
        } />
        
        {/* Protected Routes (require authentication) */}
        <Route path="/mon-compte" element={
          <ProtectedRoute>
            <MonComptePage />
          </ProtectedRoute>
        } />
        <Route path="/mon-compte/commandes/:orderId" element={
          <ProtectedRoute>
            <OrderDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/checkout" element={
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        } />
        <Route path="/checkout/success" element={
          <ProtectedRoute>
            <CheckoutSuccessPage />
          </ProtectedRoute>
        } />
        
        {/* Admin Routes (require admin role) */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        } />
        <Route path="/admin/prestations" element={
          <AdminRoute>
            <AdminPrestationsPage />
          </AdminRoute>
        } />
        <Route path="/admin/commandes" element={
          <AdminRoute>
            <AdminOrdersPage />
          </AdminRoute>
        } />
        <Route path="/admin/commandes/:orderId" element={
          <AdminRoute>
            <AdminOrderDetailPage />
          </AdminRoute>
        } />
        <Route path="/admin/press-on-nails" element={
          <AdminRoute>
            <AdminPressOnNailsPage />
          </AdminRoute>
        } />
        <Route path="/admin/featured" element={
          <AdminRoute>
            <AdminFeaturedPage />
          </AdminRoute>
        } />
        <Route path="/admin/subcategories" element={
          <AdminRoute>
            <AdminSubcategoriesPage />
          </AdminRoute>
        } />
        <Route path="/admin/gift-cards" element={
          <AdminRoute>
            <AdminGiftCardsPage />
          </AdminRoute>
        } />
        <Route path="/admin/b2b-documents" element={
          <AdminRoute>
            <AdminB2BDocumentsPage />
          </AdminRoute>
        } />
        <Route path="/admin/formations" element={
          <AdminRoute>
            <AdminFormationsPage />
          </AdminRoute>
        } />
        <Route path="/admin/reviews" element={
          <AdminRoute>
            <AdminReviewsPage />
          </AdminRoute>
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <MaintenanceProvider>
          <CartProvider>
            <AppShell />
          </CartProvider>
        </MaintenanceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppShell() {
  const location = useLocation();
  const shouldHideChrome = location.pathname === '/maintenance';

  return (
    <>
      <MaintenanceGate>
        <div className="min-h-screen flex flex-col">
          {!shouldHideChrome && <Header />}
          <main className="flex-1">
            <AnimatedRoutes />
          </main>
          {!shouldHideChrome && <Footer />}
        </div>
      </MaintenanceGate>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#FDFCF8',
            border: '1px solid #F7E7CE',
            color: '#2B2B2B',
          },
        }}
      />
    </>
  );
}

export default App;
